import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../config/api';

const Activists = () => {
  const [activists, setActivists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedActivist, setExpandedActivist] = useState(null);

  // Logo mapping for top activists (you can add logos here)
  const activistLogos = {
    'GMT Research': '/logos/gmt-research.png',
    'Viceroy Research': '/logos/viceroy-research.png',
    'The Bear Cave': '/logos/bear-cave.png',
    'Spruce Point Management': '/logos/spruce-point.png',
    'Muddy Waters Research': '/logos/muddy-waters.png',
  };

  useEffect(() => {
    fetchActivists();
  }, []);

  const fetchActivists = async () => {
    try {
      setLoading(true);
      const isVercel = window.location.hostname.includes('vercel.app');
      
      let data;
      if (isVercel) {
        const res = await fetch('/api/analysis/all?type=activist-attacks');
        data = await res.json();
      } else {
        const response = await apiClient.get('/api/analysis/activist-attacks');
        data = response.data;
      }

      const cleanedData = (data || []).map(activist => ({
        ...activist,
        activist_short_seller: activist.activist_short_seller?.replace(/<[^>]*>/g, '').trim() || 'Unknown',
        total_attacks: parseInt(activist.total_attacks) || 0,
        companies_targeted: parseInt(activist.companies_targeted) || 0,
        first_attack: activist.first_attack,
        latest_attack: activist.latest_attack,
        target_companies: activist.target_companies?.replace(/<[^>]*>/g, '').trim() || ''
      }));

      setActivists(cleanedData);
    } catch (err) {
      console.error('Error fetching activist data:', err);
      setError('Failed to fetch activist data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const totalReports = activists.reduce((sum, a) => sum + a.total_attacks, 0);
  const top5 = activists.slice(0, 5);
  const remaining = activists.slice(5);

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchActivists} 
            className="text-sm text-gray-900 border-b border-gray-900 hover:text-gray-600 hover:border-gray-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-light text-gray-900">Activist Short Sellers</h1>
          <p className="text-sm text-gray-500 mt-1">
            {activists.length} activists · {totalReports.toLocaleString()} reports
          </p>
        </div>
      </div>

      {/* Top 5 Featured Cards */}
      {!loading && top5.length > 0 && (
        <div className="mb-10">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Most Active</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-start">
            {top5.map((activist, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-5 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
                onClick={() => setExpandedActivist(expandedActivist === `top-${index}` ? null : `top-${index}`)}
              >
                {/* Logo - Centered Square */}
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 flex items-center justify-center">
                    {activistLogos[activist.activist_short_seller] ? (
                      <img 
                        src={activistLogos[activist.activist_short_seller]} 
                        alt={activist.activist_short_seller}
                        className="w-16 h-16 object-contain rounded"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                        <span className="text-xl font-bold text-gray-400">
                          {activist.activist_short_seller.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Name & Rank */}
                <div className="text-center mb-4 min-h-[3.5rem] flex flex-col justify-center">
                  <div className={`font-medium text-gray-900 mb-1 ${
                    activist.activist_short_seller.length > 20 ? 'text-xs' : 'text-sm'
                  }`}>
                    {activist.activist_short_seller}
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    index === 0 ? 'bg-yellow-100 text-yellow-800' :
                    index === 1 ? 'bg-gray-200 text-gray-700' :
                    index === 2 ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    #{index + 1}
                  </span>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-gray-50 rounded py-2">
                    <div className="text-lg font-semibold text-gray-900">{activist.total_attacks}</div>
                    <div className="text-xs text-gray-500">Reports</div>
                  </div>
                  <div className="bg-gray-50 rounded py-2">
                    <div className="text-lg font-semibold text-gray-900">{activist.companies_targeted}</div>
                    <div className="text-xs text-gray-500">Targets</div>
                  </div>
                </div>

                {/* Expanded targets */}
                {expandedActivist === `top-${index}` && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    {activist.target_companies && (
                      <>
                        <div className="text-xs text-gray-500 mb-1 text-center">Recent Targets:</div>
                        <div className="text-xs text-gray-600 leading-relaxed text-center mb-3">
                          {activist.target_companies.split(', ').slice(0, 5).join(', ')}
                          {activist.target_companies.split(', ').length > 5 && (
                            <span className="text-gray-400"> +{activist.target_companies.split(', ').length - 5} more</span>
                          )}
                        </div>
                      </>
                    )}
                    <Link
                      to={`/activist/${encodeURIComponent(activist.activist_short_seller)}`}
                      onClick={(e) => e.stopPropagation()}
                      className="block text-center text-xs font-medium text-gray-900 hover:text-gray-600 transition-colors"
                    >
                      View full profile →
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Remaining Activists Table */}
      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-medium text-gray-700">All Activists</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activist
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Reports
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Targets
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  First
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  Latest
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center text-sm text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : remaining.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-sm text-gray-500">
                    All activists shown above
                  </td>
                </tr>
              ) : (
                remaining.map((activist, index) => (
                  <React.Fragment key={index}>
                    <tr 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedActivist(expandedActivist === index + 5 ? null : index + 5)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {index + 6}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {activist.activist_short_seller}
                          </span>
                          <svg 
                            className={`w-4 h-4 text-gray-400 transition-transform ${expandedActivist === index + 5 ? 'rotate-180' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-medium text-gray-900">
                          {activist.total_attacks}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm text-gray-600">
                          {activist.companies_targeted}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {activist.first_attack ? new Date(activist.first_attack).getFullYear() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(activist.latest_attack)}
                      </td>
                    </tr>
                    {expandedActivist === index + 5 && (
                      <tr className="bg-gray-50">
                        <td colSpan="6" className="px-4 py-3">
                          {activist.target_companies && (
                            <>
                              <div className="text-xs text-gray-500 mb-1">Previous Targets:</div>
                              <div className="text-sm text-gray-700 leading-relaxed mb-2">
                                {activist.target_companies.split(', ').slice(0, 20).join(', ')}
                                {activist.target_companies.split(', ').length > 20 && (
                                  <span className="text-gray-400"> +{activist.target_companies.split(', ').length - 20} more</span>
                                )}
                              </div>
                            </>
                          )}
                          <Link
                            to={`/activist/${encodeURIComponent(activist.activist_short_seller)}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs font-medium text-gray-900 hover:text-gray-600 transition-colors"
                          >
                            View full profile →
                          </Link>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Activists;
